import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../services/api";
import { DataRequest, DataRequestStatus, User } from "../types/api";
import { RequestComments } from "./RequestComments";

interface YourRequestsPageProps {
  user: User | null;
  selectedId?: number;
}

export type RequestType = "access" | "data";
type SortBy = "created_newest" | "created_oldest" | "reviewed" | "status";

export const STATUS_GROUPS: {
  status: DataRequestStatus;
  label: string;
  labelColor: string;
}[] = [
  { status: "pending", label: "Pending", labelColor: "var(--text-warning)" },
  {
    status: "awaiting_confirmation",
    label: "Awaiting confirmation",
    labelColor: "var(--text-warning)",
  },
  {
    status: "approved",
    label: "Approved — awaiting delivery",
    labelColor: "var(--text-success)",
  },
  {
    status: "fulfilled",
    label: "Fulfilled",
    labelColor: "var(--text-success)",
  },
  {
    status: "rejected",
    label: "Rejected",
    labelColor: "var(--text-secondary)",
  },
  {
    status: "redirected",
    label: "Redirected",
    labelColor: "var(--text-secondary)",
  },
];

export const STATUS_BADGE: Record<
  DataRequestStatus,
  { bg: string; text: string; label: string }
> = {
  pending: {
    bg: "var(--bg-warning)",
    text: "var(--text-warning)",
    label: "Pending",
  },
  awaiting_confirmation: {
    bg: "var(--bg-warning)",
    text: "var(--text-warning)",
    label: "Awaiting confirmation",
  },
  approved: {
    bg: "var(--bg-success)",
    text: "var(--text-success)",
    label: "Approved — awaiting delivery",
  },
  fulfilled: {
    bg: "var(--bg-success)",
    text: "var(--text-success)",
    label: "Fulfilled",
  },
  rejected: {
    bg: "var(--surface-1)",
    text: "var(--text-muted)",
    label: "Rejected",
  },
  redirected: {
    bg: "var(--surface-1)",
    text: "var(--text-muted)",
    label: "Redirected",
  },
};

export const requestType = (request: DataRequest): RequestType =>
  request.dataset_id != null ? "access" : "data";

export const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const YourRequestsPage: React.FC<YourRequestsPageProps> = ({
  user,
  selectedId,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<DataRequest[]>([]);
  const [datasetNames, setDatasetNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(selectedId ?? null);
  const [downloading, setDownloading] = useState(false);

  const initialProject = searchParams.get("project");
  const [filterProject, setFilterProject] = useState<number | "all">(
    initialProject ? Number(initialProject) : "all",
  );
  const [filterStatus, setFilterStatus] = useState<string | "all">("all");
  // Defaults to "mine" so the page still opens on the user's own requests.
  const [filterRequester, setFilterRequester] = useState<"mine" | "all">("mine");
  const [filterType, setFilterType] = useState<RequestType | "all">("all");
  const [sortBy, setSortBy] = useState<SortBy>("created_newest");

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    if (selectedId) setSelected(selectedId);
  }, [selectedId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const [requestsResponse, datasetsResponse] = await Promise.all([
        apiService.getDataRequests(),
        apiService.getDatasets().catch(() => ({ datasets: [], total: 0 })),
      ]);
      setRequests(requestsResponse.requests || []);

      const names: Record<number, string> = {};
      (datasetsResponse.datasets || []).forEach((ds) => {
        names[ds.id] = ds.display_name;
      });
      setDatasetNames(names);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load your requests",
      );
    } finally {
      setLoading(false);
    }
  };

  const projects = useMemo(() => {
    const map = new Map<number, string>();
    requests.forEach((r) => {
      if (!map.has(r.project_id))
        map.set(r.project_id, r.project_title || `Project ${r.project_id}`);
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [requests]);

  const filteredRequests = useMemo(() => {
    let filtered = [...requests];
    if (filterRequester === "mine")
      filtered = filtered.filter((r) => r.user_id === user?.id);
    if (filterProject !== "all")
      filtered = filtered.filter((r) => r.project_id === filterProject);
    if (filterStatus !== "all")
      filtered = filtered.filter((r) => r.status === filterStatus);
    if (filterType !== "all")
      filtered = filtered.filter((r) => requestType(r) === filterType);

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "created_newest":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "created_oldest":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case "reviewed":
          if (!a.reviewed_at && !b.reviewed_at) return 0;
          if (!a.reviewed_at) return 1;
          if (!b.reviewed_at) return -1;
          return (
            new Date(b.reviewed_at).getTime() -
            new Date(a.reviewed_at).getTime()
          );
        case "status": {
          const order = [
            "pending",
            "awaiting_confirmation",
            "approved",
            "fulfilled",
            "rejected",
            "redirected",
          ];
          return order.indexOf(a.status) - order.indexOf(b.status);
        }
        default:
          return 0;
      }
    });
    return filtered;
  }, [requests, filterRequester, filterProject, filterStatus, filterType, sortBy, user?.id]);

  const grouped = useMemo(() => {
    return STATUS_GROUPS.map((g) => ({
      ...g,
      items: filteredRequests.filter((r) => r.status === g.status),
    })).filter((g) => g.items.length > 0);
  }, [filteredRequests]);

  useEffect(() => {
    if (selected == null && filteredRequests.length > 0) {
      setSelected(filteredRequests[0].id);
    }
  }, [filteredRequests, selected]);

  const selectedRequest = requests.find((r) => r.id === selected) || null;

  const handleSelect = (id: number) => {
    setSelected(id);
    navigate(`/requests/${id}`, { replace: true });
  };

  const handleDownload = async () => {
    if (!selectedRequest?.dataset_id) return;
    try {
      setDownloading(true);

      const blob = await apiService.downloadDatasetPseudonymized(
        selectedRequest.dataset_id,
        selectedRequest.project_id,
        selectedRequest.data_service_id,
        "csv",
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedRequest.title}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        err instanceof Error ? err.message : "Failed to download data file.",
      );
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p style={{ fontSize: "14px", color: "var(--text-muted)" }}>
          Loading your requests…
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <style>{`
        .yr-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
        }
        .yr-title {
          margin: 0 0 4px;
          font-size: 24px;
          color: var(--text-primary);
        }
        .yr-subtitle {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0;
        }
        .yr-new-btn {
          background: var(--fill-primary);
          color: var(--on-primary);
          border: none;
          padding: 8px 14px;
          border-radius: var(--radius);
          font-size: 14px;
          font-family: inherit;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          flex-shrink: 0;
        }
        .yr-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .yr-filters select {
          padding: 7px 10px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          background: var(--surface-2);
          color: var(--text-primary);
        }
        .yr-filters select.yr-sort {
          margin-left: auto;
        }
        .yr-split {
          display: grid;
          grid-template-columns: 340px 1fr;
          border: 0.5px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          min-height: 440px;
        }
        .yr-list-panel {
          background: var(--surface-1);
          border-right: 0.5px solid var(--border);
          padding: 0.9rem;
          overflow: auto;
        }
        .yr-group-label {
          font-size: 12px;
          font-weight: 500;
          margin: 0 0 6px;
        }
        .yr-group-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          margin-bottom: 16px;
          background: var(--surface-2);
        }
        .yr-row {
          padding: 9px 10px;
          cursor: pointer;
          border-left: 2px solid transparent;
        }
        .yr-row:not(:last-child) {
          border-bottom: 0.5px solid var(--border);
        }
        .yr-row:hover {
          background: var(--surface-1);
        }
        .yr-row.selected {
          background: var(--bg-accent-muted);
          border-left-color: var(--border-accent);
        }
        .yr-row-title {
          font-size: 13px;
          font-weight: 500;
          margin: 0;
        }
        .yr-row.selected .yr-row-title {
          color: var(--text-accent);
        }
        .yr-row-sub {
          font-size: 11px;
          color: var(--text-muted);
          margin: 2px 0 0;
        }
        .yr-type-pill {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 10px;
          color: var(--text-muted);
          background: var(--surface-1);
          padding: 1px 6px;
          border-radius: 8px;
          margin-top: 4px;
        }
        .yr-detail-panel {
          padding: 1.5rem 2rem;
        }
        .yr-detail-project {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 6px;
        }
        .yr-detail-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: var(--text-secondary);
          background: var(--surface-1);
          padding: 3px 9px;
          border-radius: var(--radius);
          margin-bottom: 8px;
        }
        .yr-detail-titlerow {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
          gap: 16px;
        }
        .yr-detail-title-label {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0 0 2px;
        }
        .yr-detail-title {
          margin: 0;
          font-size: 20px;
          color: var(--text-primary);
        }
        .yr-detail-status-badge {
          font-size: 12px;
          font-weight: 500;
          padding: 4px 10px;
          border-radius: var(--radius);
          flex-shrink: 0;
        }
        .yr-detail-desc {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 12px 0 20px;
          max-width: 520px;
        }
        .yr-detail-meta {
          display: flex;
          gap: 32px;
          padding: 14px 0;
          border-top: 0.5px solid var(--border);
          border-bottom: 0.5px solid var(--border);
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .yr-detail-meta-label {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0 0 3px;
        }
        .yr-detail-meta-value {
          font-size: 13px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }
        .yr-detail-meta-value.muted {
          font-weight: 400;
          color: var(--text-muted);
        }
        .yr-files-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0 0 6px;
        }
        .yr-files-empty {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: var(--surface-1);
          border-radius: var(--radius);
          color: var(--text-muted);
          font-size: 13px;
        }
        .yr-file-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 4px;
          border-bottom: 0.5px solid var(--border);
        }
        .yr-file-row:last-child {
          border-bottom: none;
        }
        .yr-file-name {
          font-size: 13px;
          font-family: var(--font-mono, ui-monospace, monospace);
        }
        .yr-download-btn {
          margin-top: 12px;
          background: var(--fill-primary);
          color: var(--on-primary);
          border: none;
          padding: 8px 16px;
          font-size: 13px;
          font-family: inherit;
          font-weight: 500;
          border-radius: var(--radius);
          cursor: pointer;
        }
        .yr-download-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .yr-admin-notes {
          margin-top: 20px;
          padding: 10px 14px;
          background: var(--bg-warning);
          color: var(--text-warning);
          border-radius: var(--radius);
          font-size: 13px;
          white-space: pre-wrap;
        }
        .yr-comments {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 0.5px solid var(--border);
        }
        .yr-empty {
          text-align: center;
          padding: 40px;
          color: var(--text-muted);
        }
      `}</style>

      <div className="yr-header">
        <div>
          <h1 className="yr-title">Your requests</h1>
          <p className="yr-subtitle">
            Access and data requests you've submitted, across all projects
          </p>
        </div>
        <button
          className="yr-new-btn"
          onClick={() => navigate("/requests/new")}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          New request
        </button>
      </div>

      {error && <div className="yr-empty">{error}</div>}

      {!error && requests.length === 0 ? (
        <div className="yr-empty">You haven't submitted any requests yet.</div>
      ) : (
        !error && (
          <>
            <div className="yr-filters">
              <select
                value={filterRequester}
                onChange={(e) =>
                  setFilterRequester(e.target.value as "mine" | "all")
                }
              >
                <option value="mine">My requests</option>
                <option value="all">My projects' requests</option>
              </select>
              <select
                value={filterProject}
                onChange={(e) =>
                  setFilterProject(
                    e.target.value === "all" ? "all" : Number(e.target.value),
                  )
                }
              >
                <option value="all">All projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All statuses</option>
                {STATUS_GROUPS.map((g) => (
                  <option key={g.status} value={g.status}>
                    {g.label}
                  </option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={(e) =>
                  setFilterType(e.target.value as RequestType | "all")
                }
              >
                <option value="all">All types</option>
                <option value="access">Access request</option>
                <option value="data">Data request</option>
              </select>
              <select
                className="yr-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
              >
                <option value="created_newest">Newest first</option>
                <option value="created_oldest">Oldest first</option>
                <option value="reviewed">Last reviewed</option>
                <option value="status">Status priority</option>
              </select>
            </div>

            <div className="yr-split">
              <div className="yr-list-panel">
                {grouped.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                    No requests match these filters.
                  </p>
                ) : (
                  grouped.map((group) => (
                    <div key={group.status}>
                      <p
                        className="yr-group-label"
                        style={{ color: group.labelColor }}
                      >
                        {group.label} ({group.items.length})
                      </p>
                      <div className="yr-group-list">
                        {group.items.map((request) => {
                          const type = requestType(request);
                          return (
                            <div
                              key={request.id}
                              className={`yr-row ${selected === request.id ? "selected" : ""}`}
                              onClick={() => handleSelect(request.id)}
                            >
                              <p
                                className="yr-row-title"
                                style={
                                  selected !== request.id &&
                                  request.status === "rejected"
                                    ? { color: "var(--text-muted)" }
                                    : undefined
                                }
                              >
                                {request.title}
                              </p>
                              <p className="yr-row-sub">
                                {request.project_title ||
                                  `Project ${request.project_id}`}{" "}
                                · {formatDate(request.created_at)}
                              </p>
                              <span className="yr-type-pill">
                                {type === "data" ? (
                                  <>
                                    <svg
                                      width="11"
                                      height="11"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden="true"
                                    >
                                      <path d="M8 17a5 5 0 0 1-1-9.9V7a5 5 0 0 1 10-1 4 4 0 0 1-1 7.9" />
                                      <path d="M12 12v9M9 15l3-3 3 3" />
                                    </svg>
                                    Data request
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      width="11"
                                      height="11"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden="true"
                                    >
                                      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
                                    </svg>
                                    Access request · 1 file
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="yr-detail-panel">
                {selectedRequest && (
                  <RequestDetail
                    request={selectedRequest}
                    datasetName={
                      selectedRequest.dataset_id
                        ? datasetNames[selectedRequest.dataset_id]
                        : undefined
                    }
                    onDownload={handleDownload}
                    downloading={downloading}
                    isOwnRequest={selectedRequest.user_id === user?.id}
                  />
                )}
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

interface RequestDetailProps {
  request: DataRequest;
  datasetName?: string;
  onDownload: () => void;
  downloading: boolean;
  // The fulfilled-request download grant is scoped to the requester, so offering
  // the button on a team-mate's request would only produce a 403.
  isOwnRequest: boolean;
}

const RequestDetail: React.FC<RequestDetailProps> = ({
  request,
  datasetName,
  onDownload,
  downloading,
  isOwnRequest,
}) => {
  const type = requestType(request);
  const badge =
    STATUS_BADGE[request.status as DataRequestStatus] || STATUS_BADGE.pending;
  const titleLabel = type === "data" ? "Dataset title" : "Request";

  return (
    <>
      <p className="yr-detail-project">
        {request.project_title || `Project ${request.project_id}`}
      </p>
      <span className="yr-detail-type-badge">
        {type === "data" ? (
          <>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M8 17a5 5 0 0 1-1-9.9V7a5 5 0 0 1 10-1 4 4 0 0 1-1 7.9" />
              <path d="M12 12v9M9 15l3-3 3 3" />
            </svg>
            Data request
          </>
        ) : (
          <>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
            </svg>
            Access request
          </>
        )}
      </span>

      <div className="yr-detail-titlerow">
        <div>
          <p className="yr-detail-title-label">{titleLabel}</p>
          <h2 className="yr-detail-title">{request.title}</h2>
        </div>
        <span
          className="yr-detail-status-badge"
          style={{ background: badge.bg, color: badge.text }}
        >
          {badge.label}
        </span>
      </div>

      <p className="yr-detail-desc">{request.description}</p>

      <div className="yr-detail-meta">
        <div>
          <p className="yr-detail-meta-label">Submitted</p>
          <p className="yr-detail-meta-value">
            {formatDateTime(request.created_at)}
          </p>
        </div>
        <div>
          <p className="yr-detail-meta-label">Reviewed</p>
          {request.reviewed_at ? (
            <p className="yr-detail-meta-value">
              {formatDateTime(request.reviewed_at)}
            </p>
          ) : (
            <p className="yr-detail-meta-value muted">Awaiting review</p>
          )}
        </div>
      </div>

      {type === "data" ? (
        <>
          <p className="yr-files-label">Files</p>
          <div className="yr-files-empty">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            Not uploaded yet — files will appear here once the project admin or
            data supplier uploads them.
          </div>
        </>
      ) : (
        <>
          <p className="yr-files-label">Requested files (1)</p>
          <div className="yr-file-row">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-muted)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <path d="M14 2v6h6M9 13h6M9 17h6" />
            </svg>
            <span className="yr-file-name">{datasetName || request.title}</span>
          </div>
        </>
      )}

      {request.status === "fulfilled" && request.dataset_id && isOwnRequest && (
        <button
          className="yr-download-btn"
          onClick={onDownload}
          disabled={downloading}
        >
          {downloading ? "Downloading…" : "Download data"}
        </button>
      )}

      {request.admin_notes && (
        <div className="yr-admin-notes">{request.admin_notes}</div>
      )}

      <div className="yr-comments">
        <RequestComments
          requestId={request.id}
          requestStatus={request.status}
        />
      </div>
    </>
  );
};
